import { ref } from 'vue';
import mixpanel from '@/mixpanel.ts';

type TooltipOpts = {
  dataPointIndex: number;
  seriesIndex: number;
};

// Convert chart names for download
const formatChartName = (
  origName: string,
  area: string,
  time: string,
  zone: string,
  cluster: string,
  day: string,
) => {
  let chartName = `${origName.replace(/\s/g, '')}`;

  if (area) {
    chartName += `-${area}`;
  }
  if (area === 'Cluster' && cluster) {
    chartName += `-${cluster}`;
  }
  if (area === 'Zone' && zone) {
    chartName += `-${zone}`;
  }
  if (time) {
    chartName += `-${time}`;
  }
  if (day) {
    chartName += `-${day}`;
  }

  return chartName;
};

// Simplify fetching the same name for each exportable file type
const getChartExportSettings = (
  origName: string,
  area: string,
  time: string,
  zone: string,
  cluster: string,
  day: string,
) => {
  const chartName = formatChartName(origName, area, time, zone, cluster, day);
  return {
    show: true,
    export: {
      csv: {
        filename: chartName,
      },
      svg: {
        filename: chartName,
      },
      png: {
        filename: chartName,
      },
    },
  };
};

const getDefaultChartOptions = (chartCode: string) => {
  const chartOptions = defaultChartOptions;
  chartOptions.value.chart.events = {
    click: (event: MouseEvent) => {
      const target = event.target as HTMLDivElement;
      const clickedClass = target?.classList;
      if (clickedClass.contains('exportSVG')) {
        mixpanel.track('Chart Download', {
          widget: chartCode,
          type: 'SVG',
        });
      } else if (clickedClass.contains('exportPNG')) {
        mixpanel.track('Chart Download', {
          widget: chartCode,
          type: 'PNG',
        });
      } else if (clickedClass.contains('exportCSV')) {
        mixpanel.track('Chart Download', {
          widget: chartCode,
          type: 'CSV',
        });
      }
    },
  };

  if (chartCode.includes('scorecard')) {
    chartOptions.value.grid = {
      show: true,
      borderColor: '#CCCCCC',
      strokeDashArray: 3,
      position: 'back',
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      row: {
        colors: undefined,
        opacity: 0.5,
      },
      column: {
        colors: undefined,
        opacity: 0.5,
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    };
  }

  return chartOptions;
};

type yAxisAnnotation = {
  y?: number;
  borderColor?: string;
  borderWidth?: number;
  strokeHeight?: number;
  strokeWidth?: number;
  strokeDashArray?: number;
  label?: {
    borderColor?: string;
    borderRadius?: number;
    offsetY?: number;
    offsetX?: number;
    style?: {
      background?: string;
      color?: string;
      fontFamily?: string;
      fontWeight?: number | string;
      padding?: {
        bottom?: number;
        left?: number;
        right?: number;
        top?: number;
      };
    };
    text?: string;
  };
  marker?: {
    size?: number;
  };
};

export type BaseChartOptions = {
  annotations?: {
    yaxis?: yAxisAnnotation[];
    points?: {
      x: string;
      y: number;
      label: {
        text: string;
        borderColor: string;
        style: {
          background: string;
        };
      };
      marker: {
        size: number;
      };
    }[];
  };

  chart: {
    id?: string;
    type?: string; // Should not be optional, needs to be set on each chart.
    toolbar?: {
      show?: boolean;
      export?: {
        csv: {
          filename: string;
          categoryFormatter?(val: string | number): string;
          valueFormatter?(val: string | number): string;
        };
        svg: {
          filename: string;
        };
        png: {
          filename: string;
        };
      };
    };
    parentHeightOffset?: number;
    stacked?: boolean;
    width?: string | number;
    height?: string | number;
    fontFamily?: string;
    zoom?: {
      enabled: boolean;
    };
    events?: {
      click?(event: MouseEvent): void;
    };
  };

  colors?: string[];

  dataLabels?: {
    dropShadow?: {
      enabled?: boolean;
    };
    enabled?: boolean;
    textAnchor?: string;
    formatter?(val: string | number, op?: { value: number }): string | string[];
    offsetX?: number;
    style?: {
      colors?: string[];
      fontSize?: string;
      fontWeight?: string;
    };
  };

  fill?: {
    opacity: number;
  };

  grid?: {
    show: boolean;
    borderColor?: string;
    strokeDashArray?: number;
    position?: string;
    xaxis?: {
      lines?: {
        show?: boolean;
      };
    };
    yaxis?: {
      lines?: {
        show?: boolean;
      };
    };
    row?: {
      colors?: string[] | undefined;
      opacity?: number;
    };
    column?: {
      colors?: string[] | undefined;
      opacity?: number;
    };
    padding?: {
      top?: number;
      right?: number;
      bottom?: number;
      left?: number;
    };
  };

  labels?: string[];

  legend?: {
    floating?: boolean;
    fontFamily?: string;
    horizontalAlign?: string;
    offsetY?: number;
    position?: string;
  };

  markers?: {
    colors?: string;
    strokeColors?: string;
    strokeWidth?: number;
    size: number;
  };

  plotOptions?: {
    bar?: {
      borderRadius?: number;
      borderRadiusApplication?: string;
      horizontal: boolean;
      columnWidth?: string;
    };
    pie?: {
      customScale: number;
      donut: {
        size: string;
      };
    };
    treemap?: {
      distributed: boolean;
      enableShades: boolean;
    };
  };

  stroke?: {
    curve?: string;
    width?: number;
  };

  title?: {
    align?: string;
    text?: string;
    style?: {
      color: string;
      fontFamily: string;
      fontSize: string;
      fontWeight: string;
    };
  };

  tooltip?: {
    x?: {
      formatter?(val: string | number, opts?: TooltipOpts): string;
    };
    y?: {
      formatter?(val: string | number, opts?: TooltipOpts): string;
    };
  };

  xaxis?: {
    axisTicks?: {
      show?: boolean;
    };
    categories?: (string | string[] | undefined)[];
    labels?: {
      hideOverlappingLabels?: boolean;
      rotate?: number;
      rotateAlways?: boolean;
      formatter?(val: string): string;
      format?: string;
    };
    show?: boolean;
    type?: string;
    tooltip?: {
      enabled?: boolean;
    };
    title?: {
      text?: string;
    };
  };

  yaxis?: {
    axisBorder?: {
      show?: boolean;
    };
    axisTicks?: {
      show?: boolean;
    };
    forceNiceScale?: boolean;
    labels?: {
      formatter?(val: string | number): string;
    };
    min?: number;
    max?: number;
    show?: boolean;
    title?: {
      text: string;
    };
    tickAmount?: number;
  };
};

const defaultChartOptions = ref<BaseChartOptions>({
  chart: {
    id: '',
    type: '',
    parentHeightOffset: 0,
    width: '100%',
    zoom: {
      enabled: false,
    },
    fontFamily: 'Outfit',
  },
  fill: {
    opacity: 1,
  },
  legend: {
    position: 'bottom',
  },
  grid: {
    show: false,
  },
});

const getAnnotations = (label: string, average: number, lineColor: string) => {
  return {
    yaxis: [
      {
        y: average,
        borderColor: lineColor,
        borderWidth: 3,
        strokeHeight: 3,
        strokeWidth: 3,
        strokeDashArray: 6,
        label: {
          borderColor: '#FFFFFF',
          borderRadius: 8,
          offsetY: 24,
          offsetX: -12,
          style: {
            color: '#0A0A0A',
            background: '#FFFFFF',
            fontFamily: 'Outfit',
            fontWeight: 500,
            padding: {
              left: 8,
              right: 8,
              top: 4,
              bottom: 4,
            },
          },
          text: label,
        },
      },
    ],
  };
};

export { getChartExportSettings, getDefaultChartOptions, getAnnotations };
